import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

const CATEGORIES = [
  {
    name: 'Landing Pages',
    icon: '🚀',
    ideas: [
      'SaaS product landing page with hero, features, pricing',
      'Agency portfolio with animated sections',
      'App download landing page with mockups',
      'Event registration landing page',
    ]
  },
  {
    name: 'Dashboards',
    icon: '📊',
    ideas: [
      'Analytics dashboard with charts and KPIs',
      'CRM dashboard with customer list',
      'Finance tracker with income/expenses',
      'Project management board like Trello',
    ]
  },
  {
    name: 'Games',
    icon: '🎮',
    ideas: [
      'Snake game with high score',
      'Tetris game',
      'Quiz app with timer and score',
      'Memory card matching game',
    ]
  },
  {
    name: 'Tools',
    icon: '🔧',
    ideas: [
      'Calculator with history',
      'Password generator with strength meter',
      'Color palette generator',
      'Pomodoro timer with tasks',
    ]
  },
  {
    name: 'E-commerce',
    icon: '🛍️',
    ideas: [
      'Product showcase with cart',
      'Restaurant menu with ordering',
      'Digital product store',
      'Subscription pricing page',
    ]
  },
  {
    name: 'Social',
    icon: '💬',
    ideas: [
      'Social media profile page',
      'Blog with articles and comments',
      'Community forum layout',
      'Personal portfolio with projects',
    ]
  },
]

const STYLES = [
  { id: 'dark', label: 'Dark', preview: '#0a0a0a', accent: '#e8420a' },
  { id: 'light', label: 'Light', preview: '#ffffff', accent: '#6366f1' },
  { id: 'glassmorphism', label: 'Glass', preview: '#1a1a2e', accent: '#00d4ff' },
  { id: 'claymorphism', label: 'Clay', preview: '#f0e6ff', accent: '#7c3aed' },
  { id: 'brutalist', label: 'Brutal', preview: '#ffffff', accent: '#000000' },
  { id: 'retro', label: 'Retro', preview: '#1a0a00', accent: '#ff6b00' },
  { id: 'minimal', label: 'Minimal', preview: '#fafafa', accent: '#374151' },
  { id: 'neon', label: 'Neon', preview: '#050510', accent: '#00ff88' },
]

export default function Home() {
  const { user, isPro } = useAuth()
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState('')
  const [selectedStyle, setSelectedStyle] = useState('dark')
  const [mode, setMode] = useState('build') // build | discuss
  const [loading, setLoading] = useState(false)

  async function startProject(initialPrompt) {
    if (!initialPrompt.trim()) return
    setLoading(true)
    const { data: project } = await supabase
      .from('projects')
      .insert({
        user_id: user.id,
        name: initialPrompt.slice(0, 60),
        style: selectedStyle,
        type: 'webapp',
      })
      .select().single()

    if (project) {
      navigate(`/editor/${project.id}?prompt=${encodeURIComponent(initialPrompt)}&mode=${mode}`)
    }
    setLoading(false)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: 'var(--font-disp)', fontSize: 28, color: 'var(--fire)', letterSpacing: 2 }}>🐉 DRAGONCLAW</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={() => navigate('/projects')} style={{ background: 'transparent', border: '1px solid var(--border2)', borderRadius: 'var(--r)', padding: '7px 14px', color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer' }}>
            MY PROJECTS
          </button>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: isPro ? 'var(--fire)' : 'var(--text2)', padding: '4px 10px', border: `1px solid ${isPro ? 'rgba(232,66,10,0.3)' : 'var(--border)'}`, borderRadius: 20 }}>
            {isPro ? '🔥 PRO' : 'FREE'}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h1 style={{ fontFamily: 'var(--font-disp)', fontSize: 56, color: 'var(--text)', letterSpacing: 4, marginBottom: 12, lineHeight: 1 }}>
            BUILD ANYTHING<br /><span style={{ color: 'var(--fire)' }}>WITH AI</span>
          </h1>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
            Describe your app in plain English. DRAGONclaw builds it instantly.
          </p>
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
          {['build', 'discuss'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              style={{ padding: '7px 20px', background: mode === m ? 'var(--fire)' : 'transparent', border: `1px solid ${mode === m ? 'var(--fire)' : 'var(--border2)'}`, borderRadius: 20, color: mode === m ? '#fff' : 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase' }}>
              {m === 'build' ? '⚡ Build' : '💬 Discuss'}
            </button>
          ))}
        </div>

        {mode === 'discuss' && (
          <div style={{ background: 'rgba(232,66,10,0.05)', border: '1px solid rgba(232,66,10,0.15)', borderRadius: 'var(--r)', padding: '10px 14px', marginBottom: 16, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)' }}>
            💬 Discuss mode — brainstorm and plan your app before building. The AI will help you refine your idea.
          </div>
        )}

        {/* Main prompt input */}
        <div style={{ position: 'relative', marginBottom: 24 }}>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) { e.preventDefault(); startProject(prompt) } }}
            placeholder="Describe what you want to build... e.g. 'A fitness tracking app with workout logs and progress charts'"
            style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: '18px 60px 18px 20px', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 15, resize: 'none', outline: 'none', minHeight: 80, lineHeight: 1.6, boxSizing: 'border-box' }}
            rows={3}
          />
          <button onClick={() => startProject(prompt)} disabled={loading || !prompt.trim()}
            style={{ position: 'absolute', right: 12, bottom: 12, width: 42, height: 42, background: 'var(--fire)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: !prompt.trim() ? 0.4 : 1 }}>
            {loading ? '...' : '▶'}
          </button>
        </div>

        {/* Style tokens */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text2)', letterSpacing: 2, marginBottom: 10, textTransform: 'uppercase' }}>Style</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {STYLES.map(s => (
              <button key={s.id} onClick={() => setSelectedStyle(s.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: selectedStyle === s.id ? 'rgba(232,66,10,0.1)' : 'var(--surface)', border: `1px solid ${selectedStyle === s.id ? 'var(--fire)' : 'var(--border)'}`, borderRadius: 20, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, color: selectedStyle === s.id ? 'var(--fire)' : 'var(--text2)' }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: s.preview, border: `2px solid ${s.accent}`, flexShrink: 0 }} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Idea library */}
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text2)', letterSpacing: 2, marginBottom: 16, textTransform: 'uppercase' }}>Idea Library</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {CATEGORIES.map(cat => (
              <div key={cat.name} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{cat.icon}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text)', letterSpacing: 1 }}>{cat.name.toUpperCase()}</span>
                </div>
                <div style={{ padding: '8px 0' }}>
                  {cat.ideas.map(idea => (
                    <div key={idea} onClick={() => startProject(idea + (selectedStyle !== 'dark' ? `. Style: ${selectedStyle}` : ''))}
                      style={{ padding: '7px 16px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', cursor: 'pointer', transition: 'all 0.12s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--panel)'; e.currentTarget.style.color = 'var(--fire)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text2)' }}>
                      → {idea}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
